import { Faq } from 'components/business/landing-page/FaqLink';
import { Head } from 'head/Head';
import { StoreType } from '@tock/shared/utils/connect';
import { disableBackgroundScroll, resumeBackgroundScroll } from 'utils/scrollUtils';
import { gql } from '@apollo/client';
import { useBusinessFaqsQuery } from 'generated/graphql';
import { useHistory, useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';
import FaqModal from 'components/business/faq/FaqModal';
import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';

gql`
  query BusinessFaqs @alertOwner(value: GUEST) {
    results: currentBusiness {
      id
      businessFaqs {
        id
        question
        answer
      }
    }
  }
`;

/**
 *  This is a small wrapper around useBusinessFaqsQuery that processes the data if it is ready,
 *  and passes through the `loading` result of the underlying GraphQL hook.
 */
export function useBusinessFaqs() {
  const { data, loading } = useBusinessFaqsQuery();

  const finalFaqs = useMemo(
    () => (data ? ((_.get(data, 'results.businessFaqs') || []) as Faq[]) : []),
    [data]
  );

  return { loading, hasFaqs: finalFaqs.length > 0, finalFaqs };
}

const FaqRoute: React.FC = () => {
  const { app, business } = useSelector((state: StoreType) => ({
    app: state.app,
    business: state.business,
  }));

  const { loading, finalFaqs } = useBusinessFaqs();

  const history = useHistory();
  const match = useRouteMatch();

  const handleClose = () => {
    match && match.url ? history.push(match.url.replace('/faq', '')) : history.goBack();
  };

  useEffect(() => {
    disableBackgroundScroll(document);

    return () => {
      resumeBackgroundScroll(document);
    };
  }, []);

  const metaDescription = useMemo(() => {
    return _.truncate(
      _.join(
        _.map(finalFaqs, (faq) => `${faq.question} ${faq.answer}`),
        ', '
      ) || `Frequently asked questions about ${business.business?.name}`,
      {
        length: 300,
      }
    );
  }, [finalFaqs, business]);

  return (
    <>
      <Head title={`${app.config?.business?.name} - FAQs`} description={metaDescription} />
      <FaqModal faqs={finalFaqs} loading={loading} onClose={handleClose} />
    </>
  );
};

export default FaqRoute;
